import { OrderByDirection } from "@google-cloud/firestore";
import firebase from "firebase/compat/app";
import { observable } from "mobx";
import { RouterStore } from "mobx-react-router";
// import * as functions from 'firebase-functions';
// import { deleteUserAuth } from '../../functions/src';
import { v4 as uuidv4 } from "uuid";

function validateEmail(email: string): boolean {
  if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
    return true;
  }
  return false;
}
export const userTypes = {
  ADMIN: "ADMIN",
  FREE_USER: "FREE_USER",
  STANDARD_USER: "STANDARD_USER",
  PREMIUM_USER: "PREMIUM_USER",
};
export const userLimitations = [
  {
    userType: "ADMIN",
    location: 0,
    workflow: 0,
    narrative: 0,
    contact: 0,
  },
  {
    userType: "FREE_USER",
    location: 1,
    workflow: 2,
    narrative: 5,
    contact: 5,
  },
  {
    userType: "STANDARD_USER",
    location: 3,
    workflow: 4,
    narrative: -1, //Unlimited
    contact: -1,
  },
  {
    userType: "PREMIUM_USER",
    location: -1, //Unlimited
    workflow: -1, //Unlimited
    narrative: -1, //Unlimited
    contact: -1,
  },
];

export type SignupResult =
  | "SUCCESS"
  | "INVALID_EMAIL"
  | "INVALID_PASSWORD"
  | "CONFIRM_ERROR"
  | "EMAIL_IN_USE"
  | "UNKNOWN_ERROR";

export type DeleteUserAccountStatus =
  | "SUCCESS"
  | "INVALID_EMAIL"
  | "UNKNOWN_ERROR";

export type StartTrialStatus = "SUCCESS" | "NO_SUCH_USER" | "UNKNOWN_ERROR";

export type LoginResult =
  | "SUCCESS"
  | "INVALID_EMAIL"
  | "NO_SUCH_USER"
  | "INVALID_PASSWORD"
  | "UNKNOWN_ERROR";

export type PasswordResetResult =
  | "SUCCESS"
  | "INVALID_EMAIL"
  | "NO_SUCH_USER"
  | "UNKNOWN_ERROR";

export type CreateClinicResult =
  | "INVALID_NAME"
  | "INVALID_USER"
  | "UNKNOWN_ERROR"
  | "SUCCESS";

export type AdminLoginResult =
  | "SUCCESS"
  | "INVALID_EMAIL"
  | "INVALID_PASSWORD"
  | "USER_NOT_ADMIN"
  | "NO_SUCH_USER"
  | "UNKNOWN_ERROR";

export type AdminUserUpdateResult = "SUCCESS" | "FAILED UPDATE";
export type UserUpdateResult = "SUCCESS" | "FAILED UPDATE";

export type SubscriptionPlan = {
  id: number;
  name: string;
  type: string;
  price: number;
  features: Array<string>;
};
export type UserItem = {
  uid: string;
  userData: UserData;
};

export type CustomUserItem = {
  uid: string;
  email: string;
  createdAt: string;
  userType: string;
};

export type UserData = {
  email: string;
  password: string;
  currentClinic: string;
  userType: string;
  planId: string;
  subId: string;
  displayPic: string;
  createdAt: number;
  phoneNumber?: string;
  freeTrialStartDate?: Date | null;
};

export type ClinicMetadata = {
  clinicId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export default class AccountService {
  private _db: firebase.firestore.Firestore;

  @observable
  private _user: firebase.User = null;

  @observable
  private _userData: UserData = null;

  @observable
  private _clinics = new Array<ClinicMetadata>();

  @observable
  private _currentClinic = "";

  @observable
  private _userChosenPlan = null;

  @observable
  private _planExpired = [
    {
      limit: false,
      count: null,
    },
    {
      limit: false,
      count: 1,
    },
    {
      limit: false,
      count: 10,
    },
  ];

  @observable
  private _adminuser: firebase.User = null;

  @observable
  private _userWithId: UserItem = null;

  @observable
  private _adminData: Array<UserItem> = [];

  public get adminData(): Array<UserItem> {
    return this._adminData;
  }

  public get adminUser(): firebase.User {
    return this._adminuser;
  }

  public get userChosenPlan() {
    return this._userChosenPlan;
  }

  public async setUserChosenPlan(plan: SubscriptionPlan): Promise<void> {
    this._userChosenPlan = plan;
  }

  public IsPlanExpired() {
    return this._planExpired;
  }

  public async setPlanExpiry(plan): Promise<void> {
    this._planExpired = plan;
  }
  public async setCurrentClinic(clinicId: string) {
    this._currentClinic = clinicId;
    await this._db.collection("userx").doc(this.user.uid).set(
      {
        currentClinic: clinicId,
      },
      { merge: true }
    );
  }

  public getCurrentClinicName(): string {
    if (this._currentClinic == "") {
      return "";
    }

    var found = this.clinics.find(
      (clinic) => clinic.clinicId == this._currentClinic
    );

    if (found == null) {
      return "";
    }

    return found.name;
  }

  public get clinics(): Array<ClinicMetadata> {
    return this._clinics;
  }

  public get user(): firebase.User {
    return this._user;
  }

  public get userData(): UserData {
    return this._userData;
  }

  public get userWithId(): UserItem {
    return this._userWithId;
  }

  public get currentClinic(): string {
    return this._currentClinic;
  }

  constructor(private _router: RouterStore) {}

  goBack() {
    if (this._router.history.length > 1) {
      this._router.goBack();
      return;
    }

    this._router.replace("/");
  }

  async handleAdminLogin(
    email: string,
    password: string
  ): Promise<AdminLoginResult> {
    if (!validateEmail(email)) {
      return "INVALID_EMAIL";
    }

    if (password.length < 8) {
      return "INVALID_PASSWORD";
    }

    try {
      await firebase.auth().signInWithEmailAndPassword(email, password);
      var userDoc = await this._db
        .collection("userx")
        .doc(firebase.auth().currentUser.uid)
        .get();
      if (userDoc.data() == null) {
        return "NO_SUCH_USER";
      } else if (userDoc.data().userType != "ADMIN") {
        return "USER_NOT_ADMIN";
      } else {
        this._adminuser = firebase.auth().currentUser;
        return "SUCCESS";
      }
    } catch (error) {
      if (error.code === "auth/wrong-password") {
        return "INVALID_PASSWORD";
      }

      if (error.code === "auth/user-not-found") {
        return "NO_SUCH_USER";
      }
      return "UNKNOWN_ERROR";
    }
  }

  hashFunc(str: string): number {
    for (var i = 0, h = 1779033703 ^ str.length; i < str.length; i++)
      (h = Math.imul(h ^ str.charCodeAt(i), 3432918353)),
        (h = (h << 13) | (h >>> 19));
    return (function () {
      h = Math.imul(h ^ (h >>> 16), 2246822507);
      h = Math.imul(h ^ (h >>> 13), 3266489909);
      let nn = (h ^= h >>> 16) >>> 0;
      return nn / Math.pow(2, 32);
    })();
  }

  async getDataByPage(
    pageNumber: number,
    lastDocument: any,
    searchValue: string,
    orderBy: string,
    order: OrderByDirection
  ): Promise<any> {
    try {
      const pageSize = 25; // Number of documents per page
      let query: any = this._db.collection("userx");
      query = searchValue
        ? query
            .where("email", ">=", searchValue)
            .where("email", "<=", searchValue + "\uf8ff")
            .orderBy(orderBy, order)
            .limit(pageSize)
        : query.orderBy(orderBy, order).limit(pageSize);

      // let query = this._db
      //   .collection("userx")
      //   .orderBy(orderBy, order)
      //   .limit(pageSize);

      let snapshot;
      let documents;
      let lastDocument;

      for (let i = 0; i < pageNumber; i++) {
        if (i === 0) {
          snapshot = await query.get();
        } else {
          snapshot = await query.startAfter(lastDocument).get();
        }
        documents = await snapshot.docs;
        lastDocument = snapshot.docs[snapshot.docs.length - 1];
      }

      // const documents = snapshot.docs.map((doc) => doc.data());
      documents = documents
        .filter((doc) => doc.data().userType != "ADMIN")
        .map((doc) => {
          let docData = doc.data();
          if (docData.createdAt == null) {
            let rnd = this.hashFunc(docData.email);
            let d1 = new Date(2020, 3, 28).getTime();
            let d2 = new Date(2020, 9, 2).getTime();
            docData.createdAt = d1 + (d2 - d1) * rnd;
          }
          return {
            uid: doc.id,
            userData: docData as any,
          };
        });

      return {
        documents,
        lastDocument: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      console.log("Error getting data:", error);
      throw error;
    }
  }

  async reloadAdminData(pageNumber: number, lastDoc: any) {
    try {
      this._adminuser = firebase.auth().currentUser;
      // var adminData = await this._db.collection("userx").limit(25).get();
      //localhost:1234/admin
      var adminData = null;

      if (pageNumber === 1) {
        adminData = await this._db
          .collection("userx")
          .orderBy("createdAt", "desc")
          .limit(25)
          .get();
      } else {
        adminData = await this._db
          .collection("userx")
          .orderBy("createdAt", "desc")
          .startAfter("uid", lastDoc.uid)
          .limit(25)
          .get();
      }

      this._adminData = [];
      adminData.docs.forEach((doc, i) => {
        if (doc.data().userType != "ADMIN") {
          let docData = doc.data();
          if (docData.createdAt == null) {
            let rnd = this.hashFunc(docData.email);
            let d1 = new Date(2020, 3, 28).getTime();
            let d2 = new Date(2020, 9, 2).getTime();
            docData.createdAt = d1 + (d2 - d1) * rnd;
          }
          this._adminData.push({
            uid: doc.id,
            userData: docData as any,
          });
        }
      });
    } catch (error) {
      console.log(error);
    }
  }

  async fetchUserData(userId: string): Promise<any> {
    try {
      var userDoc = await this._db.collection("userx").doc(userId).get();
      return userDoc.data() as any;
    } catch (err) {
      return null;
    }
  }

  async handleLogin(email: string, password: string): Promise<LoginResult> {
    if (!validateEmail(email)) {
      return "INVALID_EMAIL";
    }

    if (password.length < 8) {
      return "INVALID_PASSWORD";
    }

    try {
      await firebase.auth().signInWithEmailAndPassword(email, password);
      this._user = firebase.auth().currentUser;
      var userDoc = await this._db
        .collection("userx")
        .doc(this._user.uid)
        .get();
      this._userData = userDoc.data() as any;

      if (this._userData == null) {
        let userData = {
          email: email,
          password: password,
          currentClinic: "",
          userType: "FREE_USER",
          planId: "",
          subId: "",
          displayPic: "",
          createdAt: Date.now(),
          freeTrialStartDate: null,
        };
        await this._db.collection("userx").doc(this._user.uid).set(userData);

        this._userData = userData;
      } else {
        let userData = {
          email: email,
          password: password,
        };
        await this._db.collection("userx").doc(this._user.uid).update(userData);
        this._userData = Object.assign(this._userData, userData);

        if (!this._userData?.hasOwnProperty("freeTrialStartDate")) {
          await firebase
            .firestore()
            .collection("userx")
            .doc(this._user?.uid)
            .update({
              freeTrialStartDate: null,
            });

          this._userData = Object.assign(this._userData, {
            freeTrialStartDate: null,
          });
        }
      }

      this._currentClinic = this._userData.currentClinic;

      await this.fetchClinicsById();

      return "SUCCESS";
    } catch (error) {
      if (error.code === "auth/wrong-password") {
        return "INVALID_PASSWORD";
      }

      if (error.code === "auth/user-not-found") {
        return "NO_SUCH_USER";
      }
      return "UNKNOWN_ERROR";
    }
  }

  async handleLogout(): Promise<void> {
    await firebase.auth().signOut();
    this._user = null;
    this._adminuser = null;
  }

  async handlePasswordReset(email: string): Promise<PasswordResetResult> {
    if (!validateEmail(email)) {
      return "INVALID_EMAIL";
    }
    try {
      await firebase.auth().sendPasswordResetEmail(email);
      return "SUCCESS";
    } catch (error) {
      if (error.code === "auth/user-not-found") {
        return "NO_SUCH_USER";
      }
      return "UNKNOWN_ERROR";
    }
  }

  async handleSignUp(
    email: string,
    password: string,
    confirmPassword: string
  ): Promise<SignupResult> {
    if (!validateEmail(email)) {
      return "INVALID_EMAIL";
    }

    if (password.length < 8) {
      return "INVALID_PASSWORD";
    }

    if (password != confirmPassword) {
      return "CONFIRM_ERROR";
    }

    try {
      await firebase.auth().createUserWithEmailAndPassword(email, password);
      this._user = firebase.auth().currentUser;
      var userData = {
        email: email,
        password: password,
        currentClinic: "",
        userType: "FREE_USER",
        // userType: "PREMIUM_USER",
        // offerPlan: true,
        planId: "",
        subId: "",
        displayPic: "",
        createdAt: Date.now(),
        freeTrialStartDate: null,
      };
      await this._db.collection("userx").doc(this._user.uid).set(userData);
      this._userData = userData;
      this._currentClinic = "";
      let state = await this.createClinic("Sample Clinic").then((res) => res);
      await this.fetchClinicsById();
      return "SUCCESS";
    } catch (error) {
      console.log(error);

      if (error.code == "auth/weak-password") {
        return "INVALID_PASSWORD";
      }

      if (error.code == "auth/email-already-in-use") {
        return "EMAIL_IN_USE";
      }
      return "UNKNOWN_ERROR";
    }
  }

  async fetchClinics(): Promise<void> {
    var user = this._user;

    if (user == null) {
      return;
    }

    var result = await this._db
      .collection("clinics")
      .where("users", "array-contains", user.email)
      .get();
    this._clinics = [];

    result.docs.forEach((doc) => {
      this._clinics.push(doc.data() as any);
    });

    if (this._currentClinic == "" && this._clinics.length != 0) {
      this._currentClinic = this._clinics[0].clinicId;
    }
    let userPlan = userLimitations.find(
      (plan) => this._userData.userType === plan.userType
    );
    if (userPlan.location <= this._clinics.length && userPlan.location !== -1) {
      this._planExpired[0].limit = true;
      this._planExpired[0].count = userPlan.location;
    } else {
      this._planExpired[0].limit = false;
      this._planExpired[0].count = -1;
    }
  }

  async fetchClinicsById(): Promise<void> {
    var user = this._user;

    if (user == null) {
      return;
    }

    var result = await this._db
      .collection("clinics")
      .where("users", "array-contains", user.uid)
      .get();
    this._clinics = [];

    result.docs.forEach((doc) => {
      this._clinics.push(doc.data() as any);
    });

    if (this._currentClinic == "" && this._clinics.length != 0) {
      this._currentClinic = this._clinics[0].clinicId;
    }
    let userPlan = userLimitations.find(
      (plan) => this._userData.userType === plan.userType
    );
    if (userPlan.location <= this._clinics.length && userPlan.location !== -1) {
      this._planExpired[0].limit = true;
      this._planExpired[0].count = userPlan.location;
    } else {
      this._planExpired[0].limit = false;
      this._planExpired[0].count = -1;
    }
  }

  async updateClinicName(id: string, clinicName: string) {
    if (id == null || id.length == 0) {
      return "UNKNOWN_ERROR";
    }

    if (this.user == null) {
      return "UNKNOWN_ERROR";
    }

    let newClinicData = {
      name: clinicName,
    };
    try {
      await this._db.collection("clinics").doc(id).update(newClinicData);

      return "SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async updateClinicData() {
    var user = this._user;
    if (user == null) {
      return;
    }

    try {
      var result = await this._db
        .collection("clinics")
        .where("users", "array-contains", user.email)
        .get();
      const userData = result.docs.map((doc) => doc.data());
      if (userData) {
        const newUserData = await Promise.all(
          userData.map(async (item) => {
            if (item.users[1]) {
              return;
            }
            const newData = {
              ...item,
              users: [...item.users, user.uid],
            };
            await this._db
              .collection("clinics")
              .doc(item.clinicId)
              .update(newData);
            return {
              ...item,
              users: [...user.email, user.uid],
            };
          })
        );
      }
    } catch (err) {
      console.log(err);
    }
  }

  async createClinic(name: string): Promise<CreateClinicResult> {
    if (name.length < 4) {
      return "INVALID_NAME";
    }

    var user = this._user;

    if (user == null) {
      return "INVALID_USER";
    }
    var clinicId = uuidv4();
    var currentDate = new Date().toString();

    try {
      var clinicMeta = {
        clinicId: clinicId,
        name: name,
        createdAt: currentDate,
        updatedAt: currentDate,
        users: [user.email, user.uid],
      };
      await this._db.collection("clinics").doc(clinicId).set(clinicMeta);
      this._clinics.unshift(clinicMeta);
      this._currentClinic = clinicId;
      await this._db.collection("userx").doc(this.user.uid).set(
        {
          currentClinic: clinicId,
        },
        { merge: true }
      );

      return "SUCCESS";
    } catch (error) {
      console.log(error);
      return "UNKNOWN_ERROR";
    }
  }

  async deleteClinic(clinicId: string) {
    const { uid } = this._user;
    try {
      const filesDoc = await firebase
        .firestore()
        .collection(`file-desc-${uid}`)
        .where("clinic", "==", clinicId)
        .get();
      const files = filesDoc.docs.map((doc) => doc.data());
      if (files.length > 0) {
        files.forEach(async (file) => {
          await firebase
            .firestore()
            .collection(`file-data-${uid}`)
            .doc(file.fileId)
            .delete();
          await firebase
            .firestore()
            .collection(`file-desc-${uid}`)
            .doc(file.fileId)
            .delete();
        });
      }
      await firebase.firestore().collection("clinics").doc(clinicId).delete();
      await this.fetchClinicsById();
    } catch (err) {
      console.log(err);
    }
  }

  async handleDeleteUser(uid: string): Promise<void> {
    let userEmail = (
      await firebase.firestore().collection("userx").doc(uid).get()
    ).data().email;
    let userClinics = await firebase
      .firestore()
      .collection("clinics")
      .where("users", "array-contains", uid)
      .get();
    userClinics.docs.map((clinic) =>
      firebase.firestore().collection("clinics").doc(clinic.id).delete()
    );

    firebase
      .firestore()
      .collection("userx")
      .doc(uid)
      .delete()
      .then(function () {
        console.log("Successfully deleted user from database");
      })
      .catch(function (error) {
        console.log("Error deleting user:", error);
      });
    var funcs = firebase.functions();
    var deleteUser = funcs.httpsCallable("deleteUserAuth");
    deleteUser({ uid: uid })
      .then((res) => {
        console.log("Successfully deleted user from Firebase auth");
      })
      .catch(function (error) {
        console.log("Error deleting user:", error);
      });
  }

  async updateUserType(
    uid: string,
    type: string
  ): Promise<AdminUserUpdateResult> {
    let newUserTypeData = {
      userType: type,
    };
    try {
      await this._db.collection("userx").doc(uid).update(newUserTypeData);
      return "SUCCESS";
    } catch (err) {
      console.log({ err });
      return "FAILED UPDATE";
    }
  }

  async saveUserProfilePic(picture): Promise<UserUpdateResult> {
    var storageRef = firebase.storage().ref();
    var db = firebase.firestore();
    let picId = "";
    const type = picture.type.split("/")[1];
    // let picDBUrl = 'images/' + this._user.uid + '_pic' + type;
    let picDBUrl = `images/${this._user.uid}_pic.${type}`;
    try {
      let uploadTask = storageRef.child(picDBUrl).put(picture);
      uploadTask.on(firebase.storage.TaskEvent.STATE_CHANGED, (snapshot) => {
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log("Upload is " + progress + "% done");
        switch (snapshot.state) {
          case firebase.storage.TaskState.PAUSED: // or 'paused'
            break;
          case firebase.storage.TaskState.RUNNING: // or 'running'
            break;
        }
      });
      await uploadTask;

      let downloadUrl = await uploadTask.snapshot.ref.getDownloadURL();

      await db
        .collection("userx")
        .doc(this.user.uid)
        .update({ displayPic: downloadUrl });
      this._userData.displayPic = downloadUrl;
      return "SUCCESS";
    } catch (ex) {
      // this.errorMessage = "Failed to upload video: "+ ex.message;
      console.log("Failed to upload Image: " + ex.message);
      return "FAILED UPDATE";
    }
  }

  async deleteUserAccount(): Promise<DeleteUserAccountStatus> {
    try {
      const user = firebase.auth().currentUser;
      if (user) {
        // Delete the user
        await user.delete();
        return "SUCCESS";
      } else {
        return "UNKNOWN_ERROR";
      }
    } catch (error) {
      console.error("Error deleting user:", error.message);
      return "UNKNOWN_ERROR";
    }
  }

  async startFreeTrial(uid: string): Promise<StartTrialStatus> {
    try {
      await firebase.firestore().collection("userx").doc(uid).update({
        freeTrialStartDate: Date.now(),
      });
      return "SUCCESS";
    } catch (error) {
      console.log("Failed to start free trials:", error.message);
      return "UNKNOWN_ERROR";
    }
  }

  async initialize(): Promise<void> {
    this._db = firebase.firestore();

    let loggedInUser = null;
    var promise = new Promise((resolve) => {
      var unsub = firebase.auth().onAuthStateChanged((user) => {
        loggedInUser = user;
        unsub();
        resolve(null);
      });
    });
    await promise;

    if (loggedInUser == null) {
      return;
    }

    var userDoc = await this._db
      .collection("userx")
      .doc(loggedInUser.uid)
      .get();
    if (userDoc.data()) {
      if (userDoc.data().userType != "ADMIN") {
        this._user = loggedInUser;
        this._userData = userDoc.data() as any;
        this._currentClinic = this._userData.currentClinic;

        await this.fetchClinicsById();
      } else {
        this._adminuser = loggedInUser;
      }
    }
  }
}
